
import { supabase } from '@/integrations/supabase/client';
import type { Database } from '@/integrations/supabase/types';

type NotificationType = Database['public']['Enums']['notification_type'];
type NotificationPriority = Database['public']['Enums']['notification_priority'];

export interface NotificationTrigger {
  type: NotificationType;
  data: Record<string, any>;
  userId?: string;
  priority?: NotificationPriority;
}

export interface NotificationRecipient {
  id: string;
  email: string;
  role: string;
  preferences?: Record<string, boolean>;
}

class NotificationDispatcher {
  async dispatch(trigger: NotificationTrigger): Promise<void> {
    console.log(`Dispatching notification: ${trigger.type}`, trigger.data);

    try {
      // Determine recipients based on notification type
      const recipients = await this.getRecipients(trigger);
      
      // Process notifications for each recipient
      for (const recipient of recipients) {
        await this.processNotificationForRecipient(trigger, recipient);
      }
    } catch (error) {
      console.error('Error dispatching notification:', error);
    }
  }

  private async getRecipients(trigger: NotificationTrigger): Promise<NotificationRecipient[]> {
    const recipients: NotificationRecipient[] = [];

    // Customer-specific notifications
    if (this.isCustomerNotification(trigger.type) && trigger.userId) {
      const customer = await this.getCustomerRecipient(trigger.userId);
      if (customer) recipients.push(customer);
    }

    // Admin notifications
    if (this.isAdminNotification(trigger.type)) {
      const admins = await this.getAdminRecipients();
      recipients.push(...admins);
    }

    return recipients;
  }

  private async getCustomerRecipient(userId: string): Promise<NotificationRecipient | null> {
    const { data: profile } = await supabase
      .from('profiles')
      .select('id, email, full_name, role')
      .eq('id', userId)
      .single();

    if (!profile?.email) return null;

    // Get customer preferences
    const { data: preferences } = await supabase
      .from('customer_notification_preferences')
      .select('notification_type, enabled')
      .eq('user_id', userId);

    const prefs = preferences?.reduce((acc, pref) => {
      acc[pref.notification_type] = pref.enabled;
      return acc;
    }, {} as Record<string, boolean>) || {};

    return {
      id: profile.id,
      email: profile.email,
      role: profile.role,
      preferences: prefs
    };
  }

  private async getAdminRecipients(): Promise<NotificationRecipient[]> {
    const { data: profiles } = await supabase
      .from('profiles')
      .select('id, email, full_name, role')
      .in('role', ['admin', 'manager']);

    return profiles?.map(profile => ({
      id: profile.id,
      email: profile.email || '',
      role: profile.role,
      preferences: {} // Admins get all notifications
    })) || [];
  }

  private async processNotificationForRecipient(
    trigger: NotificationTrigger, 
    recipient: NotificationRecipient
  ): Promise<void> {
    // Check customer preferences (admins bypass this)
    if (recipient.role === 'customer' && !this.shouldSendToCustomer(trigger.type, recipient.preferences)) {
      return;
    }

    // Create database notification (in-app notification)
    await this.createDatabaseNotification(trigger, recipient);

    // Email sending removed - handled by database triggers + queue processor
    // Database triggers insert into notification_queue, cron job processes queue every 5 minutes
    console.log(`✅ Created in-app notification for ${recipient.email}. Email will be sent via queue processor.`);

    // Send additional notifications for admins
    if (recipient.role === 'admin' || recipient.role === 'manager') {
      await this.sendAdminChannelNotifications(trigger, recipient);
    }
  }

  private shouldSendToCustomer(type: NotificationType, preferences: Record<string, boolean> = {}): boolean {
    // Default to true if preference not explicitly set
    return preferences[type] !== false;
  }

  private async createDatabaseNotification(
    trigger: NotificationTrigger, 
    recipient: NotificationRecipient
  ): Promise<void> {
    const template = await this.getNotificationTemplate(trigger.type, recipient.role);
    
    const { title, message } = this.renderTemplate(template, trigger.data);

    await supabase.from('notifications').insert({
      user_id: recipient.id,
      type: trigger.type,
      title,
      message,
      metadata: trigger.data,
      priority: trigger.priority || 'medium',
      category: this.getNotificationCategory(trigger.type)
    });
  }

  private async sendEmailNotification(
    trigger: NotificationTrigger, 
    recipient: NotificationRecipient
  ): Promise<void> {
    try {
      // Build structured template data based on notification type
      const templateData = this.buildTemplateData(trigger, recipient);

      // Call email edge function with proper structure
      const { error } = await supabase.functions.invoke('send-notification-email', {
        body: {
          notificationId: crypto.randomUUID(),
          to: recipient.email,
          notificationType: trigger.type,
          templateData
        }
      });

      if (error) {
        console.error('Error sending email notification:', error);
      }
    } catch (error) {
      console.error('Failed to send email notification:', error);
    }
  }

  private buildTemplateData(trigger: NotificationTrigger, recipient: NotificationRecipient): Record<string, any> {
    const baseData = {
      recipientName: trigger.data.customerName || trigger.data.recipientName || recipient.email.split('@')[0],
      recipientEmail: recipient.email
    };

    switch (trigger.type) {
      case 'order_created':
        return {
          ...baseData,
          orderNumber: trigger.data.orderNumber,
          orderDate: trigger.data.orderDate || new Date().toLocaleDateString(),
          totalAmount: trigger.data.totalAmount,
          items: trigger.data.items || [],
          shippingAddress: trigger.data.shippingAddress,
          estimatedDelivery: trigger.data.estimatedDelivery
        };

      case 'order_updated':
        // Determine specific status-based type
        const status = trigger.data.newStatus || trigger.data.status;
        return {
          ...baseData,
          orderNumber: trigger.data.orderNumber,
          status,
          trackingNumber: trigger.data.trackingNumber,
          estimatedDelivery: trigger.data.estimatedDelivery,
          carrier: trigger.data.carrier
        };

      case 'review_approved':
        return {
          ...baseData,
          productName: trigger.data.productName,
          rating: trigger.data.rating,
          reviewTitle: trigger.data.reviewTitle,
          productUrl: trigger.data.productUrl
        };

      case 'review_rejected':
        return {
          ...baseData,
          productName: trigger.data.productName,
          rejectionReason: trigger.data.rejectionReason,
          guidelinesUrl: trigger.data.guidelinesUrl
        };

      default:
        return {
          ...baseData,
          ...trigger.data
        };
    }
  }

  private async sendAdminChannelNotifications(
    trigger: NotificationTrigger, 
    recipient: NotificationRecipient
  ): Promise<void> {
    // Future: Add Slack, SMS, or other admin notification channels
    console.log(`Would send admin channel notification for ${trigger.type} to ${recipient.email}`);
  }

  private async getNotificationTemplate(type: NotificationType, role: string) {
    const templateKey = `${type}_${role}`;
    return this.getDefaultTemplate(templateKey, type);
  }

  private async getEmailTemplate(type: NotificationType, role: string) {
    const { data: template } = await supabase
      .from('notification_templates')
      .select('*')
      .eq('type', type)
      .eq('is_active', true)
      .single();

    return template;
  }

  private renderTemplate(template: any, data: Record<string, any>) {
    const title = template?.title || this.getDefaultTitle(data.type);
    const message = template?.message || this.getDefaultMessage(data.type, data);

    return {
      title: this.replaceVariables(title, data),
      message: this.replaceVariables(message, data)
    };
  }

  private renderEmailTemplate(template: any, data: Record<string, any>, recipient: NotificationRecipient) {
    const subject = template?.subject_template || this.getDefaultTitle(data.type);
    const body = template?.html_template || template?.body_template || this.getDefaultMessage(data.type, data);

    return {
      subject: this.replaceVariables(subject, { ...data, recipientName: recipient.email }),
      body: this.replaceVariables(body, { ...data, recipientName: recipient.email })
    };
  }

  private replaceVariables(template: string, data: Record<string, any>): string {
    return template.replace(/\{\{(\w+)\}\}/g, (match, key) => {
      return data[key]?.toString() || match;
    });
  }

  private getDefaultTemplate(key: string, type: NotificationType) {
    const defaults = {
      [`${type}_customer`]: {
        title: this.getDefaultTitle(type),
        message: this.getDefaultMessage(type, {})
      },
      [`${type}_admin`]: {
        title: `Admin: ${this.getDefaultTitle(type)}`,
        message: `Admin notification: ${this.getDefaultMessage(type, {})}`
      }
    };

    return defaults[key] || defaults[`${type}_customer`];
  }

  private getDefaultTitle(type: NotificationType): string {
    const titles: Partial<Record<NotificationType, string>> = {
      order_created: 'Order Confirmed!',
      order_updated: 'Order Status Updated',
      order_payment_failed: 'Payment Issue',
      product_created: 'New Product Added',
      product_updated: 'Product Updated',
      product_low_stock: 'Low Stock Alert',
      product_out_of_stock: 'Out of Stock',
      user_registered: 'Welcome!',
      system_error: 'System Alert',
      security_alert: 'Security Alert',
      order_high_value: 'High Value Order'
    };

    return titles[type] || 'Notification';
  }

  private getDefaultMessage(type: NotificationType, data: Record<string, any>): string {
    const messages: Partial<Record<NotificationType, string>> = {
      order_created: 'Your order has been confirmed and is being processed.',
      order_updated: 'Your order status has been updated to {{status}}.',
      order_payment_failed: 'There was an issue with your payment. Please update your payment method.',
      product_created: 'A new product "{{productName}}" has been added.',
      product_updated: 'Product "{{productName}}" has been updated.',
      product_low_stock: 'Product "{{productName}}" is running low on stock ({{quantity}} remaining).',
      product_out_of_stock: 'Product "{{productName}}" is now out of stock.',
      user_registered: 'Welcome to our store! Your account has been created successfully.',
      system_error: 'A system error has occurred: {{error}}',
      security_alert: 'Security alert: {{message}}',
      order_high_value: 'High value order received: ${{amount}}'
    };

    return messages[type] || 'You have a new notification.';
  }

  private getNotificationCategory(type: NotificationType): string {
    const categories: Partial<Record<NotificationType, string>> = {
      order_created: 'order',
      order_updated: 'order',
      order_payment_failed: 'payment',
      product_created: 'product',
      product_updated: 'product',
      product_low_stock: 'inventory',
      product_out_of_stock: 'inventory',
      user_registered: 'account',
      system_error: 'system',
      security_alert: 'security',
      order_high_value: 'order'
    };

    return categories[type] || 'general';
  }

  private isCustomerNotification(type: NotificationType): boolean {
    const customerTypes: NotificationType[] = [
      'order_created',
      'order_updated',
      'order_payment_failed',
      'user_registered'
    ];
    return customerTypes.includes(type);
  }

  private isAdminNotification(type: NotificationType): boolean {
    const adminTypes: NotificationType[] = [
      'product_created',
      'product_updated',
      'product_low_stock',
      'product_out_of_stock',
      'order_created',
      'order_high_value',
      'user_registered',
      'system_error',
      'security_alert'
    ];
    return adminTypes.includes(type);
  }
}

// Export singleton instance
export const notificationDispatcher = new NotificationDispatcher();

// Convenience function for dispatching notifications
export async function dispatchNotification(
  type: NotificationType,
  data: Record<string, any>,
  userId?: string,
  priority: NotificationPriority = 'medium'
): Promise<void> {
  await notificationDispatcher.dispatch({
    type,
    data,
    userId,
    priority
  });
}
